// @flow strict

/**
 * Data transcribed from the exported Claude Design source (Mrunal - Home).
 * Positions, sizes, timings and copy are the design's own values; only the
 * click targets have been repointed at this site's real routes.
 */

// Tier sizing lives in Constellation.module.scss rather than here, because the
// portrait layout needs different values and a media query cannot override an
// inline style. Tier only selects a class now.

// The four-pointed star each node is drawn as.
export const STAR_PATH = 'M12 0.5 C13.1 8.7 15.3 10.9 23.5 12 C15.3 13.1 13.1 15.3 12 23.5 '
  + 'C10.9 15.3 8.7 13.1 0.5 12 C8.7 10.9 10.9 8.7 12 0.5 Z';

// `descAbove` flips the hover caption above the node, for nodes low on the page.
export const NODES = [
  {
    key: 'ai',
    label: 'AI',
    tier: 1,
    left: '41%',
    top: '26%',
    drift: 'driftA',
    duration: '19s',
    delay: '0s',
    desc: 'Building products with intelligent systems.',
    to: '/articles'
  },
  {
    key: 'product',
    label: 'Product',
    tier: 1,
    left: '30%',
    top: '9%',
    drift: 'driftB',
    duration: '21s',
    delay: '1.6s',
    desc: 'Designing things people love.',
    to: '/articles'
  },
  {
    key: 'ideas',
    label: 'Ideas',
    tier: 1,
    left: '62%',
    top: '12%',
    drift: 'driftD',
    duration: '23s',
    delay: '.4s',
    desc: 'Questions worth chasing.',
    to: '/reflections'
  },
  {
    key: 'tech',
    label: 'Tech',
    tier: 2,
    left: '11%',
    top: '19%',
    drift: 'driftC',
    duration: '22s',
    delay: '.8s',
    desc: 'How things work.',
    to: '/articles'
  },
  {
    key: 'business',
    label: 'Business',
    tier: 2,
    left: '12.5%',
    top: '52%',
    drift: 'driftD',
    duration: '24s',
    delay: '3.3s',
    desc: 'How good ideas find their footing.',
    to: '/articles'
  },
  {
    key: 'poetry',
    label: 'Poetry',
    tier: 2,
    left: '78%',
    top: '27%',
    drift: 'driftA',
    duration: '20s',
    delay: '2.2s',
    desc: 'Trying to express what prose cannot.',
    to: '/poetry'
  },
  {
    key: 'life',
    label: 'Life',
    tier: 2,
    left: '63%',
    top: '82%',
    drift: 'driftD',
    duration: '21s',
    delay: '1.9s',
    desc: 'Notes from everyday living.',
    descAbove: true,
    to: '/reflections'
  },
  {
    key: 'coffee',
    label: 'Coffee',
    tier: 3,
    left: '87%',
    top: '46%',
    drift: 'driftC',
    duration: '18s',
    delay: '1.1s',
    desc: 'Small rituals. Big conversations.',
    to: '/reflections'
  },
  {
    key: 'travel',
    label: 'Travel',
    tier: 3,
    left: '82%',
    top: '68%',
    drift: 'driftB',
    duration: '22s',
    delay: '.6s',
    desc: 'Collecting places, not checklists.',
    to: '/photographs'
  },
  {
    key: 'running',
    label: 'Running',
    tier: 3,
    left: '44%',
    top: '90%',
    drift: 'driftC',
    duration: '20s',
    delay: '2.6s',
    desc: 'Thinking one kilometre at a time.',
    descAbove: true,
    to: '/reflections'
  },
  {
    key: 'yoga',
    label: 'Yoga',
    tier: 3,
    left: '26%',
    top: '82%',
    drift: 'driftA',
    duration: '24s',
    delay: '1.3s',
    desc: 'Breath before everything else.',
    descAbove: true,
    to: '/reflections'
  },
  {
    key: 'food',
    label: 'Food',
    tier: 3,
    left: '78%',
    top: '88%',
    drift: 'driftA',
    duration: '23s',
    delay: '3.6s',
    desc: 'Every city, one good meal at a time.',
    descAbove: true,
    to: '/reflections'
  },
  {
    key: 'plants',
    label: 'Plants',
    tier: 3,
    left: '88%',
    top: '88%',
    drift: 'driftB',
    duration: '21s',
    delay: '2.9s',
    desc: 'Slow growth, patiently watched.',
    descAbove: true,
    to: '/reflections'
  }
];

// Each link names the two nodes it joins, so hovering a node can light up
// every edge that touches it. `weight` picks one of three stroke treatments.
export const LINKS = [
  { key: 'businessTech', from: 'business', to: 'tech', d: 'M125,354 Q88,214 110,129', weight: 'normal' },
  { key: 'techAi', from: 'tech', to: 'ai', d: 'M110,129 Q240,134 410,177', weight: 'strong' },
  { key: 'techProduct', from: 'tech', to: 'product', d: 'M110,129 Q198,56 300,61', weight: 'faint' },
  { key: 'aiProduct', from: 'ai', to: 'product', d: 'M410,177 Q338,88 300,61', weight: 'strong' },
  { key: 'businessAi', from: 'business', to: 'ai', d: 'M125,354 Q252,312 410,177', weight: 'faint' },
  { key: 'aiIdeas', from: 'ai', to: 'ideas', d: 'M410,177 Q522,88 620,82', weight: 'strong' },
  { key: 'productIdeas', from: 'product', to: 'ideas', d: 'M300,61 Q460,22 620,82', weight: 'normal' },
  { key: 'ideasPoetry', from: 'ideas', to: 'poetry', d: 'M620,82 Q720,110 780,184', weight: 'normal' },
  { key: 'poetryCoffee', from: 'poetry', to: 'coffee', d: 'M780,184 Q852,226 870,313', weight: 'normal' },
  { key: 'coffeeIdeas', from: 'coffee', to: 'ideas', d: 'M870,313 Q938,128 620,82', weight: 'faint' },
  { key: 'ideasLife', from: 'ideas', to: 'life', d: 'M620,82 Q812,300 630,558', weight: 'faint' },
  { key: 'coffeeTravel', from: 'coffee', to: 'travel', d: 'M870,313 Q906,394 820,462', weight: 'normal' },
  { key: 'travelLife', from: 'travel', to: 'life', d: 'M820,462 Q748,552 630,558', weight: 'normal' },
  { key: 'coffeeLife', from: 'coffee', to: 'life', d: 'M870,313 Q788,440 630,558', weight: 'faint' },
  { key: 'lifeRunning', from: 'life', to: 'running', d: 'M630,558 Q536,622 440,612', weight: 'normal' },
  { key: 'runningYoga', from: 'running', to: 'yoga', d: 'M440,612 Q348,598 260,558', weight: 'normal' },
  { key: 'lifePlants', from: 'life', to: 'plants', d: 'M630,558 Q760,586 880,598', weight: 'normal' },
  { key: 'travelFood', from: 'travel', to: 'food', d: 'M820,462 Q818,548 780,598', weight: 'faint' },
  { key: 'foodLife', from: 'food', to: 'life', d: 'M780,598 Q704,592 630,558', weight: 'normal' }
];

export const LINK_STROKE = { faint: '.7', normal: '1', strong: '1.2' };

/**
 * Portrait arrangement for phones, where the wide desktop ellipse cannot fit.
 * Values are percentages of the field. Nodes are ordered roughly by cluster
 * down the page — Build near the top, then Create, then Live — so the descent
 * reads as a progression rather than a scatter.
 */
// Ordered so that nodes joined by an edge stay near each other going down the
// page — following the same adjacency the desktop ring uses — which keeps the
// edges from crossing each other into a tangle.
export const PORTRAIT = {
  product: { x: 62, y: 5 },
  tech: { x: 25, y: 10 },
  ai: { x: 45, y: 18 },
  ideas: { x: 70, y: 26 },
  business: { x: 20, y: 27 },
  poetry: { x: 52, y: 36 },
  coffee: { x: 24, y: 44 },
  travel: { x: 68, y: 50 },
  life: { x: 45, y: 62 },
  food: { x: 78, y: 62 },
  plants: { x: 72, y: 78 },
  running: { x: 40, y: 80 },
  yoga: { x: 20, y: 88 }
};

// The lowest node's caption would run past the bottom of the field.
export const PORTRAIT_DESC_ABOVE = ['yoga'];

// The portrait field is about twice as tall as it is wide, so the viewBox
// matches that to keep the curves from stretching.
export const PORTRAIT_VIEWBOX = { w: 100, h: 190 };

/**
 * Rebuilds every edge for a given position map, so a layout only has to say
 * where its thirteen nodes sit — moving one redraws each edge that touches it,
 * and no two layouts can drift out of sync. Each edge is a quadratic with a
 * small perpendicular bend, alternating direction so the field looks organic
 * rather than like a wire diagram.
 *
 * Positions are percentages of the field in both axes; the y values are scaled
 * into the viewBox here so a map can be read and edited as plain percentages.
 */
const linksFor = (positions, viewBox) => {
  const yScale = viewBox.h / 100;

  return LINKS.map((link, i) => {
    const a = positions[link.from];
    const b = positions[link.to];
    const ax = a.x;
    const ay = a.y * yScale;
    const bx = b.x;
    const by = b.y * yScale;
    const bend = (i % 2 === 0 ? 1 : -1) * 0.08;
    const cx = ((ax + bx) / 2) - ((by - ay) * bend);
    const cy = ((ay + by) / 2) + ((bx - ax) * bend);

    return {
      ...link,
      d: `M${ax},${ay.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${bx},${by.toFixed(1)}`
    };
  });
};

export const PORTRAIT_LINKS = linksFor(PORTRAIT, PORTRAIT_VIEWBOX);

/**
 * Landscape arrangement, used by V2 on anything wider than a phone.
 *
 * The field fills the width of its column and the ratio derives the height, so
 * this map is what decides how much of the page the constellation takes and
 * how much is left for the writing below it. At 2:1 it is a wide,
 * shallow band — shallow enough that the three cards underneath clear the fold
 * on a laptop, which is the constraint that set this ratio.
 *
 * These positions are not placed by hand. Three hand-built arrangements in a
 * row came out lopsided — a dense clump on one side, dangling leaf nodes, and
 * a silhouette that read as a creature rather than a sky. They are the output
 * of a force-directed layout instead: every pair of nodes repels, every edge
 * pulls like a spring, and a weak gravity keeps the whole thing compact. That
 * is what produces even spacing without a hand deciding each point.
 *
 * Many random starts were scored and the best kept, on: how uniform the edge
 * lengths are, how even each node's distance to its nearest neighbour is, how
 * few edges cross, whether any edge passes through someone else's label, how
 * much of the box is occupied, and whether Build/Create/Live still read left
 * to right. A last pass rotated near-vertical and near-horizontal edges off
 * axis, since perfectly square angles look drawn rather than found.
 *
 * Because the box's ratio matches the viewBox, one unit is the same number of
 * pixels on both axes and the curves are never sheared. Nothing sits past x 91
 * or y 82: that is the margin the hover captions need to stay inside the field.
 */
export const LANDSCAPE = {
  // Build
  product: { x: 18.3, y: 35.2 },
  ai: { x: 21.1, y: 64.4 },
  tech: { x: 9.0, y: 56.6 },
  business: { x: 12.1, y: 81.2 },
  // Create
  ideas: { x: 40.3, y: 43.6 },
  poetry: { x: 43.6, y: 14.7 },
  // Live
  coffee: { x: 55.9, y: 23.8 },
  travel: { x: 74.5, y: 30.0 },
  plants: { x: 72.0, y: 82.0 },
  life: { x: 67.8, y: 51.0 },
  food: { x: 85.5, y: 64.6 },
  running: { x: 91.0, y: 38.6 },
  yoga: { x: 88.0, y: 14.7 }
};

export const LANDSCAPE_VIEWBOX = { w: 100, h: 50 };

export const LANDSCAPE_LINKS = linksFor(LANDSCAPE, LANDSCAPE_VIEWBOX);

/**
 * Ambient stars, drawn small and faint behind the named ones so the field
 * reads as a sky rather than a diagram. They carry no links and no captions.
 *
 * Also generated rather than placed: a Poisson-style scatter that keeps every
 * ambient star at least 77px from any node — clear of the labels — and 61px
 * from its neighbours, so they fill the gaps evenly without clumping. They are
 * tied to the node positions above; move a node and these want regenerating.
 * Landscape only: the portrait field is too tight to take them.
 */
export const LANDSCAPE_STARDUST = [
  { x: 54.1, y: 7.4, size: 4 }, { x: 36.3, y: 7.6, size: 5 }, { x: 95.2, y: 9.2, size: 6 },
  { x: 64.3, y: 11.5, size: 4 }, { x: 9.1, y: 13.2, size: 5 }, { x: 16.2, y: 15.6, size: 4 },
  { x: 33.3, y: 18.6, size: 6 }, { x: 2.5, y: 18.6, size: 5 }, { x: 22.3, y: 19.6, size: 4 },
  { x: 5.2, y: 30.1, size: 5 }, { x: 84.1, y: 31.1, size: 6 }, { x: 26.4, y: 31.4, size: 4 },
  { x: 45.6, y: 32.0, size: 5 }, { x: 57.3, y: 40.7, size: 4 }, { x: 29.0, y: 42.4, size: 6 },
  { x: 2.9, y: 42.7, size: 5 }, { x: 82.3, y: 48.2, size: 4 }, { x: 50.2, y: 52.9, size: 5 },
  { x: 75.1, y: 56.6, size: 6 }, { x: 32.3, y: 57.7, size: 4 }, { x: 47.5, y: 64.8, size: 5 },
  { x: 39.2, y: 65.2, size: 4 }, { x: 93.0, y: 67.1, size: 6 }, { x: 78.0, y: 67.9, size: 5 },
  { x: 55.9, y: 73.4, size: 4 }, { x: 2.9, y: 76.9, size: 5 }, { x: 64.5, y: 77.0, size: 6 },
  { x: 31.8, y: 78.5, size: 4 }, { x: 42.8, y: 79.4, size: 5 }, { x: 96.5, y: 79.9, size: 4 },
  { x: 52.4, y: 83.8, size: 6 }, { x: 82.3, y: 90.0, size: 5 }, { x: 62.1, y: 90.3, size: 4 },
  { x: 4.7, y: 90.6, size: 5 }, { x: 23.4, y: 90.7, size: 6 }, { x: 91.5, y: 93.9, size: 4 }
].map((star, i) => ({
  ...star,
  key: `dust${i}`,
  // Spread across the cycle, and give each star its own range, so the field
  // shimmers unevenly instead of pulsing in unison.
  duration: `${5 + ((i * 7) % 9)}s`,
  delay: `${((i * 13) % 11) * 0.4}s`,
  lo: (0.12 + ((i * 3) % 5) * 0.03).toFixed(2),
  hi: (0.5 + ((i * 5) % 7) * 0.05).toFixed(2)
}));

// Ambient marks that bleed off the edges of the hero, behind everything else.
export const BACKDROP_PATHS = [
  'M110,129 Q-30,84 -140,148',
  'M300,61 Q322,-34 258,-104',
  'M870,313 Q1040,296 1136,362',
  'M440,612 Q418,704 486,772',
  'M-60,470 Q30,516 84,600',
  'M1060,120 Q940,180 966,238',
  'M700,660 Q800,700 900,660'
];

export const BACKDROP_DOTS = [
  { cx: -46, cy: 236, r: 2.5 },
  { cx: 1064, cy: 512, r: 2.5 },
  { cx: 176, cy: -40, r: 2.5 },
  { cx: 546, cy: 700, r: 2.5 },
  { cx: 980, cy: 52, r: 2 }
];

// Loose groupings the corner labels respond to.
export const CLUSTERS = [
  { key: 'build', label: 'Build', left: '3%', top: '5%', members: ['ai', 'tech', 'product', 'business'] },
  { key: 'create', label: 'Create', left: '68%', top: '3%', members: ['ideas', 'poetry'] },
  {
    // The export placed this at top 88%, level with the Plants node at 88%,
    // where the two labels collide at every width. Lifted clear of it.
    key: 'live',
    label: 'Live',
    left: '90%',
    top: '80%',
    members: ['life', 'travel', 'coffee', 'yoga', 'running', 'plants', 'food']
  }
];

export const NAV = [
  { label: 'Writing', to: '/articles' },
  { label: 'Journal', to: '/reflections' },
  { label: 'Photography', to: '/photographs' },
  { label: 'Poetry', to: '/poetry' }
];

export const NEIGHBOURS = LINKS.reduce((acc, link) => {
  (acc[link.from] = acc[link.from] || []).push(link.to);
  (acc[link.to] = acc[link.to] || []).push(link.from);
  return acc;
}, {});
